import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Home, ShoppingBag } from "lucide-react";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { Footer } from "./Footer";

const STATUS_CONFIG = {
  success: {
    icon: CheckCircle2,
    iconColor: "text-green-600",
    iconBg: "bg-green-100",
    title: "¡Pago aprobado!",
    subtitle: "Gracias por tu compra en Burbujas de mami 🍯",
    message:
      "Hemos recibido tu pago correctamente. En breve te contactaremos por correo o WhatsApp para coordinar el envío de tus productos artesanales.",
    primaryLabel: "Seguir comprando",
    testId: "payment-success",
  },
  failure: {
    icon: XCircle,
    iconColor: "text-red-600",
    iconBg: "bg-red-100",
    title: "Pago no completado",
    subtitle: "No pudimos procesar tu pago",
    message:
      "No te preocupes, no se realizó ningún cargo. Puedes intentar de nuevo con otro método de pago o contactarnos si necesitas ayuda.",
    primaryLabel: "Intentar de nuevo",
    testId: "payment-failure",
  },
  pending: {
    icon: Clock,
    iconColor: "text-amber-600",
    iconBg: "bg-amber-100",
    title: "Pago en proceso",
    subtitle: "Estamos confirmando tu pago",
    message:
      "Tu pago está siendo procesado. Te enviaremos un correo en cuanto se confirme. Si pagaste en OXXO u otro medio en efectivo, recuerda completar el pago en las próximas horas.",
    primaryLabel: "Volver al inicio",
    testId: "payment-pending",
  },
};

export const PaymentResult = ({ status = "success" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.success;
  const Icon = config.icon;

  const paymentId = searchParams.get("payment_id");
  const externalReference = searchParams.get("external_reference");
  const mpStatus = searchParams.get("status");

  useEffect(() => {
    // Clear cart only on success
    if (status === "success") {
      localStorage.removeItem("burbujasCart");
      window.dispatchEvent(new CustomEvent("cartUpdate", { detail: 0 }));
    }
    window.scrollTo(0, 0);
  }, [status]);

  const handlePrimaryAction = () => {
    if (status === "failure") {
      navigate("/checkout");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-amber-50/40 to-white">
      <Header cartItemsCount={0} onCartOpen={() => {}} />

      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div
          className="max-w-xl w-full bg-white rounded-3xl shadow-xl border border-amber-100 p-8 md:p-12 text-center"
          data-testid={config.testId}
        >
          <div
            className={`w-20 h-20 ${config.iconBg} rounded-full flex items-center justify-center mx-auto mb-6`}
          >
            <Icon className={`w-12 h-12 ${config.iconColor}`} strokeWidth={1.8} />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {config.title}
          </h1>
          <p className="text-lg text-amber-800 font-medium mb-6">
            {config.subtitle}
          </p>
          <p className="text-gray-600 leading-relaxed mb-8">{config.message}</p>

          {(paymentId || externalReference) && (
            <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-4 mb-8 text-left text-sm">
              <p className="font-semibold text-gray-700 mb-2">
                Detalles del pago
              </p>
              {paymentId && (
                <p className="text-gray-600">
                  <span className="font-medium">ID de pago:</span> {paymentId}
                </p>
              )}
              {externalReference && (
                <p className="text-gray-600 break-all">
                  <span className="font-medium">Referencia:</span>{" "}
                  {externalReference}
                </p>
              )}
              {mpStatus && (
                <p className="text-gray-600">
                  <span className="font-medium">Estado:</span> {mpStatus}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handlePrimaryAction}
              className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-6 rounded-full"
              data-testid={`${config.testId}-primary-btn`}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {config.primaryLabel}
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="border-amber-700 text-amber-800 hover:bg-amber-50 px-6 py-6 rounded-full"
              data-testid={`${config.testId}-home-btn`}
            >
              <Home className="w-4 h-4 mr-2" />
              Ir al inicio
            </Button>
          </div>

          {status === "failure" && (
            <p className="text-xs text-gray-500 mt-6">
              ¿Necesitas ayuda? Escríbenos a través del formulario de contacto.
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentResult;
